/**
 * Module-level auth state for bootstrap: single source of truth for /api/auth/me.
 * - Ensures /api/auth/me is called at most once per bootstrap (single-flight + 60s fresh).
 * - Prevents auth state flip/flop (unauth→auth→unauth) from refetch/race.
 * - Subscribers (useAuthMe) re-render when state changes.
 */

export type AuthStatus = 'unknown' | 'loading' | 'authenticated' | 'unauthenticated' | 'offline' | 'degraded';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  username?: string;
  phone?: string;
  role?: string;
  department?: string;
  position?: string;
  avatar_url?: string;
  created_at?: string;
  last_login?: string;
  [key: string]: unknown;
}

export interface AuthState {
  status: AuthStatus;
  user: UserProfile | null;
  lastCheckedAt: number | undefined;
  error: Error | null;
}

const initialState: AuthState = {
  status: 'unknown',
  user: null,
  lastCheckedAt: undefined,
  error: null,
};

let state: AuthState = { ...initialState };
const listeners = new Set<() => void>();

const FRESH_MS = 60_000; // 60s – don't refetch if last check within this

function getState(): AuthState {
  return state;
}

function setState(partial: Partial<AuthState>, reason?: string): void {
  const prevStatus = state.status;
  state = { ...state, ...partial };
  if (import.meta.env.DEV && prevStatus !== state.status) {
    try {
      console.groupCollapsed(
        `[traceAuth] status: ${prevStatus} → ${state.status}`,
        reason ?? ''
      );
      console.log('reason', reason, 'user', state.user?.id, 'lastCheckedAt', state.lastCheckedAt);
      console.groupEnd();
    } catch (_) {
      /* noop */
    }
  }
  listeners.forEach((cb) => cb());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Call when token is removed (logout / 401). */
export function setUnauthenticated(): void {
  setState(
    {
      status: 'unauthenticated',
      user: null,
      lastCheckedAt: undefined,
      error: null,
    },
    'setUnauthenticated'
  );
}

export function setOffline(error?: Error | null): void {
  setState(
    {
      status: 'offline',
      error: error ?? null,
    },
    'setOffline'
  );
}

export function setDegraded(error?: Error | null): void {
  setState(
    {
      status: 'degraded',
      error: error ?? null,
    },
    'setDegraded'
  );
}

/** Call after login so next bootstrap fetches /auth/me. */
export function resetForNewSession(): void {
  setState(
    {
      status: 'unknown',
      user: null,
      lastCheckedAt: undefined,
      error: null,
    },
    'resetForNewSession'
  );
}

/** Call when profile is updated (e.g. Profile page) so UI stays in sync. */
export function setAuthUser(user: UserProfile | null): void {
  setState(
    {
      user,
      status: user ? 'authenticated' : 'unauthenticated',
      lastCheckedAt: user ? Date.now() : undefined,
      error: null,
    },
    'setAuthUser'
  );
}

export const authStore = {
  getState,
  setState,
  subscribe,
  setUnauthenticated,
  setOffline,
  setDegraded,
  resetForNewSession,
  setAuthUser,
};

export function isAuthFresh(): boolean {
  if (state.lastCheckedAt == null) return false;
  return Date.now() - state.lastCheckedAt < FRESH_MS;
}
